import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowDownLeft, ArrowUpRight, Calculator, Copy, Send } from "lucide-react";
import { toast } from "sonner";
import { faDateTime, faNum } from "@/lib/format";
import { CalculatorModal } from "@/components/CalculatorModal";
import { CoinMark } from "@/components/Header";
import { Sparkline } from "@/components/Sparkline";
import { COIN_IMAGES } from "@/lib/coinImages";

const buildShareText = (coins, market) => {
  const date = new Intl.DateTimeFormat("fa-IR", { dateStyle: "full", timeZone: "Asia/Tehran" }).format(new Date());
  return [
    "✦ تابلوی نرخ روز — سکه کازرانی ✦",
    `📅 ${date}`,
    "",
    ...(coins || []).map((c) => `${c.name} — خرید: ${faNum(c.buy)} | فروش: ${faNum(c.sell)}`),
    "",
    "— شاخص‌های بازار —",
    ...(market || []).map((m) => `${m.name}: ${faNum(m.price)} ${m.unit}`),
    "",
    "قیمت‌ها به تومان · نرخ نهایی هنگام معامله در فروشگاه",
    "📍 تهران، خیابان ولیعصر، بین فاطمی و یوسف‌آباد، پلاک ۱۹۶۴",
    "☎ ۰۲۱۸۸۹۷۷۸۲۴",
  ].join("\n");
};

export const RateBoard = ({ coins, updatedAt, history, market }) => {
  const [calcOpen, setCalcOpen] = useState(false);
  const navigate = useNavigate();

  const copyRates = async () => {
    try {
      await navigator.clipboard.writeText(buildShareText(coins, market));
      toast.success("متن نرخ‌های امروز کپی شد — آماده ارسال در واتساپ یا تلگرام");
    } catch {
      toast.error("کپی ناموفق بود");
    }
  };
  return (
    <section id="rates" data-testid="gold-coin-board" className="mx-auto max-w-7xl scroll-mt-28 px-4 py-20 sm:px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="mb-10 flex flex-wrap items-end justify-between gap-4"
      >
        <div>
          <span className="text-xs tracking-widest text-amber-500/70">تابلوی نرخ سکه</span>
          <h2 className="mt-2 text-base font-bold text-amber-100 md:text-lg">نرخ خرید و فروش سکه</h2>
          <p data-testid="rates-updated" className="mt-2 text-xs text-stone-500">
            آخرین به‌روزرسانی: {faDateTime(updatedAt)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="copy-rates-btn"
            onClick={copyRates}
            className="flex items-center gap-2 rounded-full border border-amber-500/30 px-4 py-2.5 text-xs font-medium text-amber-200 transition-colors duration-300 hover:border-amber-400 hover:bg-amber-500/10"
          >
            <Copy className="h-3.5 w-3.5" />
            کپی نرخ‌ها
          </button>
          <a
            data-testid="share-whatsapp-btn"
            href={`https://wa.me/?text=${encodeURIComponent(buildShareText(coins || [], market || []))}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-emerald-500/30 px-4 py-2.5 text-xs font-medium text-emerald-300 transition-colors duration-300 hover:bg-emerald-500/10"
          >
            <Send className="h-3.5 w-3.5" />
            اشتراک در واتساپ
          </a>
          <button
            data-testid="open-calculator-btn"
            onClick={() => setCalcOpen(true)}
            className="gold-sheen flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-5 py-2.5 text-sm font-medium text-amber-200 transition-colors duration-300 hover:bg-amber-500 hover:text-black"
          >
            <Calculator className="h-4 w-4" />
            محاسبه‌گر سریع
          </button>
        </div>
      </motion.div>

      <div className="glass overflow-hidden rounded-2xl">
        <div className="hidden grid-cols-12 gap-4 border-b border-amber-500/15 px-6 py-4 text-xs text-stone-500 sm:grid">
          <span className="col-span-5">نوع سکه</span>
          <span className="col-span-3 text-center">خرید از مشتری</span>
          <span className="col-span-3 text-center">فروش به مشتری</span>
          <span className="col-span-1 text-center">تغییر</span>
        </div>
        {coins?.length ? (
          coins.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`coin-row-${c.id}`}
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.05 }}
              onClick={() => navigate(`/coin/${c.id}`)}
              className="group grid cursor-pointer grid-cols-2 items-center gap-4 border-b border-amber-500/8 px-6 py-4 transition-colors duration-300 last:border-0 hover:bg-amber-500/5 sm:grid-cols-12"
            >
              <div className="col-span-2 flex items-center gap-3 sm:col-span-5">
                {COIN_IMAGES[c.id] ? (
                  <img src={COIN_IMAGES[c.id]} alt={c.name} className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-amber-500/40 transition-transform duration-500 group-hover:scale-110" />
                ) : (
                  <CoinMark className="h-10 w-10 shrink-0 transition-transform duration-500 group-hover:rotate-[25deg]" />
                )}
                <span>
                  <span className="block text-sm font-semibold text-amber-100">{c.name}</span>
                  <span className="block text-[11px] text-stone-500">{c.sub}</span>
                </span>
                {(() => {
                  const pts = (history || []).map((h) => h.coins?.[c.id]).filter((v) => v);
                  return pts.length > 1 ? (
                    <Sparkline testid={`coin-spark-${c.id}`} points={pts} width={110} height={30} stroke="#d4af37" className="mr-auto hidden shrink-0 lg:block" />
                  ) : null;
                })()}
              </div>
              <div className="text-center sm:col-span-3">
                <span className="block text-[10px] text-stone-500 sm:hidden">خرید</span>
                <span data-testid={`coin-buy-${c.id}`} className="text-base font-bold text-stone-200">
                  {faNum(c.buy)}
                  <small className="mr-1 text-[10px] font-normal text-stone-500">تومان</small>
                </span>
              </div>
              <div className="text-center sm:col-span-3">
                <span className="block text-[10px] text-stone-500 sm:hidden">فروش</span>
                <span data-testid={`coin-sell-${c.id}`} className="text-base font-bold text-amber-300">
                  {faNum(c.sell)}
                  <small className="mr-1 text-[10px] font-normal text-stone-500">تومان</small>
                </span>
              </div>
              <div className="col-span-2 flex flex-col items-center justify-center gap-1.5 sm:col-span-1">
                <span
                  data-testid={`coin-change-${c.id}`}
                  className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${c.change >= 0 ? "bg-emerald-500/10 text-emerald-400" : "bg-red-500/10 text-red-400"}`}
                >
                  {c.change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownLeft className="h-3 w-3" />}
                  {faNum(Math.abs(c.change))}٪
                </span>
                {typeof c.bubble === "number" && c.bubble !== 0 && (
                  <span
                    data-testid={`coin-bubble-${c.id}`}
                    title="اختلاف قیمت بازار با ارزش ذاتی طلای سکه"
                    className={`rounded-full px-2 py-0.5 text-[10px] ${c.bubble > 20 ? "bg-red-500/10 text-red-400" : c.bubble > 8 ? "bg-amber-500/10 text-amber-300" : "bg-emerald-500/10 text-emerald-400"}`}
                  >
                    حباب ٪{faNum(c.bubble)}
                  </span>
                )}
              </div>
            </motion.div>
          ))
        ) : (
          <div className="animate-pulse px-6 py-10 text-center text-sm text-stone-500">در حال دریافت نرخ‌ها…</div>
        )}
      </div>
      <p className="mt-4 text-center text-[11px] text-stone-600">
        قیمت‌ها به تومان و صرفاً جهت اطلاع است · نرخ نهایی هنگام معامله در مغازه اعلام می‌شود · حباب = اختلاف قیمت با ارزش ذاتی طلای سکه
      </p>
      <CalculatorModal open={calcOpen} onOpenChange={setCalcOpen} coins={coins || []} />
    </section>
  );
};
